import React, { useState, useEffect } from "react";
import { Button } from "../../elements/Button/Button";
import { Paragraph } from "../../elements/Paragraph/Paragraph";
import useMobileBreakpoint from "@arctheme-hooks/useMobileBreakpoint";

export const ShopifyMigrationClients = ({ settings }) => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const isMobile = useMobileBreakpoint(settings.settings.globals.mobile_breakpoint);
  
  // Extract blocks that are enabled
  const slides = settings.settings.blocks?.filter(block => block.type === 'client_slide') || [];

  const totalSlides = slides.length;

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % totalSlides);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
  };

  const currentSlideData = slides[currentSlide] || {};

  return (
    <div className="shopify-migration-clients relative w-full overflow-hidden"
      style={{
        minHeight: isMobile 
          ? `${settings.settings.section.mobile_min_height || 0}px` 
          : `${settings.settings.section.desktop_min_height || 0}px`
      }}
    >
      <div className="flex flex-col md:flex-row w-full gap-x-[140px]">
        {/* Left content */}
        <div className="w-full lg:w-1/2 flex flex-col">
          <h2 className={`${settings.settings.section.title_font_style || ''} mb-12 lg:mb-[100px]`}>
            {settings.settings.section.title}
          </h2>

          {isMobile && slides.length > 0 && currentSlideData.settings?.image_mobile && (
            <img 
              src={currentSlideData.settings?.image_mobile}
              alt={currentSlideData.settings?.content_title || 'Client showcase'} 
              className="w-full h-full object-cover mb-12"
              loading="lazy"
            />
          )}
          
          {slides.length > 0 && (
            <div className="slide-content lg:flex lg:gap-[55px] transition-all duration-500">
              <button 
                onClick={prevSlide} 
                className="w-[50px] h-[50px] rounded-full hidden lg:flex items-center justify-center border border-[#1C1C1F] shrink-0 translate-y-[100px]"
                aria-label="Previous slide"
              >
                <span className="sr-only">Previous</span>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M15 18l-6-6 6-6"/>
                </svg>
              </button>

              <div>
                <h3 className={`${currentSlideData.settings?.title_font_style || ''} mb-2`}>
                  {currentSlideData.settings?.content_title || ''}
                </h3>

                <div className="slide-indicator mb-8 flex">
                  <span className="font-medium">{currentSlide + 1}</span>
                  <span className="mx-2">/</span>
                  <span>{totalSlides}</span>
                </div>
                
                <Paragraph 
                  text={currentSlideData.settings?.content_description || ''}
                  type={currentSlideData.settings?.description_font_style || ''}
                  classes="mb-6"
                />
                
                {currentSlideData.settings?.cta_text && (
                  <Button 
                    label={currentSlideData.settings?.cta_text}
                    style={currentSlideData.settings?.cta_style || 'primary'}
                    link={currentSlideData.settings?.cta_url || '#'}
                    icon={currentSlideData.settings?.cta_icon}
                  />
                )}
              </div>

              <button 
                onClick={nextSlide}
                className="w-[50px] h-[50px] rounded-full hidden lg:flex items-center justify-center border border-[#1C1C1F] shrink-0 translate-y-[100px]"
                aria-label="Next slide"
              >
                <span className="sr-only">Next</span>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 18l6-6-6-6"/>
                </svg>
              </button>
                
              <div className="flex lg:hidden justify-center mt-8 gap-8">
                <button 
                  onClick={prevSlide} 
                  className="w-[50px] h-[50px] rounded-full flex items-center justify-center border border-[#1C1C1F] mr-2"
                  aria-label="Previous slide"
                >
                  <span className="sr-only">Previous</span>
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M15 18l-6-6 6-6"/>
                  </svg>
                </button>
                <button 
                  onClick={nextSlide}
                  className="w-[50px] h-[50px] rounded-full flex items-center justify-center border border-[#1C1C1F]"
                  aria-label="Next slide"
                >
                  <span className="sr-only">Next</span>
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 18l6-6-6-6"/>
                  </svg>
                </button>
              </div>
            </div>
          )}
        </div>
        
        {/* Right content - Image/Video */}
        {!isMobile && 
          <div className="w-full lg:w-1/2 relative min-h-[300px] md:min-h-[400px]">
            {slides.length > 0 && currentSlideData.settings?.image && (
              <img 
                src={currentSlideData.settings?.image}
                alt={currentSlideData.settings?.content_title || 'Client showcase'} 
                className="w-full h-full object-cover"
                loading="lazy"
              />
            )}
            {slides.length > 0 && currentSlideData.settings?.video_url && (
              <video 
                src={currentSlideData.settings?.video_url}
                autoPlay
                loop
                muted
                playsInline
                className="w-full h-full object-cover"
              />
            )}
            
            {/* Decorative background element similar to the design */}
            <div className="absolute bottom-0 right-0 w-3/4 h-12 bg-gradient-to-r from-transparent to-gray-100 z-10"></div>
          </div>
        }
      </div>
    </div>
  );
};

export default ShopifyMigrationClients;